import axios from 'axios';

// Proxy in package.json forwards /api/* → http://localhost:5000/api/*

// const API = axios.create({ baseURL: '/api' });
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

export const getFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:') || path.startsWith('data:')) {
    return path;
  }
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const host = baseUrl.replace(/\/api\/?$/, '');
  return `${host}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Attach JWT token to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on 401
API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
// POST /api/auth/login      body: { email, password }  → { token, data: User }
// POST /api/auth/register   body: { name, email, password, role }
// GET  /api/auth/me         → { data: User }
export const authAPI = {
  login:    (data) => API.post('/auth/login', data),
  register: (data) => API.post('/auth/register', data),
  me:       ()     => API.get('/auth/me'),
  forgotPassword: (data) => API.post('/auth/forgot-password', data),
  resetPassword: (token, data) => API.post(`/auth/reset-password/${token}`, data),
  resetPasswordDirect: (data) => API.post('/auth/reset-password-direct', data),
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
// GET /api/dashboard/summary
//   → { data: { customers, projects:{total,active}, tasks:{total,open},
//               leads, deals:{total,won}, revenue, hours_logged } }
// GET /api/dashboard/tasks-by-status   → { data: [{status, count}] }
// GET /api/dashboard/deals-pipeline    → { data: [{stage, count, total_value}] }
// GET /api/dashboard/recent-activities → { data: Activity[] }
export const dashboardAPI = {
  summary:          () => API.get('/dashboard/summary'),
  tasksByStatus:    () => API.get('/dashboard/tasks-by-status'),
  dealsPipeline:    () => API.get('/dashboard/deals-pipeline'),
  recentActivities: () => API.get('/dashboard/recent-activities'),
};

// ── AI Assistant ─────────────────────────────────────────────────────────────
// POST /api/ai-assistant/chat     body: { message, history }  → { data: { reply } }
// POST /api/ai-assistant/refresh  clears the cached data snapshot so the next
//                                  question re-reads fresh projects/tasks/deadlines
export const aiAssistantAPI = {
  chat:    (message, history) => API.post('/ai-assistant/chat', { message, history }),
  refresh: ()                 => API.post('/ai-assistant/refresh'),
};


// ── Projects ──────────────────────────────────────────────────────────────────
// Model: project_id | project_name | start_date | end_date | status | customer_id
// Status: Planning | Active | On Hold | Completed | Cancelled
// GET    /api/projects      → includes customer, tasks[]
// POST   /api/projects      body: { project_name, start_date, end_date, status, customer_id }  Admin|Manager
// PUT    /api/projects/:id  Admin|Manager
// DELETE /api/projects/:id  Admin only
export const projectsAPI = {
  list:   ()          => API.get('/projects'),
  get:    (id)        => API.get(`/projects/${id}`),
  create: (data)      => API.post('/projects', data),
  update: (id, data)  => API.put(`/projects/${id}`, data),
  approveMilestone: (id, milestoneIndex) => API.patch(`/projects/${id}/milestones/${milestoneIndex}/approve`),
  complete: (id, data) => API.patch(`/projects/${id}/complete`, data),
  delete: (id)        => API.delete(`/projects/${id}`),
  activityLogs: (params) => API.get('/projects/activity-logs', { params }),
  startWorkSession: (id) => API.post(`/projects/${id}/work-sessions/start`),
  stopWorkSession: (id) => API.post(`/projects/${id}/work-sessions/stop`),
  getWorkSessionStatus: (id) => API.get(`/projects/${id}/work-sessions/status`),
  getWorkSessionSummary: (id) => API.get(`/projects/${id}/work-sessions/summary`),
};

// ── Feedback ──────────────────────────────────────────────────────────────────
// Ticket model: feedback_id | project_id | client_id | subject | message | category |
//               module_name | priority | attachments[] | status
// Category: General Feedback | Bug Fix / Correction | Change Request / Scope Update | General Inquiry
// Priority: Low | Medium | High | Urgent
// Status:   Open | In Review | In Progress | Needs Clarification | Resolved
// Thread message model: message_id | feedback_id | sender_id | body | attachments[]
// POST /api/feedback              body: { project_id, subject, message, category, module_name, priority, attachments } Client
// GET  /api/feedback/my           params: project_id?, category?, priority?, status?                                  Client
// GET  /api/feedback              params: project_id?, category?, priority?, status?                                  Admin|Manager
// GET  /api/feedback/:id          → ticket + full ordered thread
// POST /api/feedback/:id/messages body: { body, attachments }   → adds a thread reply/follow-up
// PUT  /api/feedback/:id/status   body: { status }              Admin|Manager
export const feedbackAPI = {
  create:       (data)        => API.post('/feedback', data),
  listMine:     (params)      => API.get('/feedback/my', { params }),
  listAll:      (params)      => API.get('/feedback', { params }),
  get:          (id)          => API.get(`/feedback/${id}`),
  addMessage:   (id, data)    => API.post(`/feedback/${id}/messages`, data),
  updateStatus: (id, status)  => API.put(`/feedback/${id}/status`, { status }),
};
// ── Tasks ─────────────────────────────────────────────────────────────────────
// Model: task_id | task_name | description | status | priority | project_id | assigned_to
// Status: Todo | In Progress | Review | Done
// Priority: Low | Medium | High | Critical
// GET    /api/tasks         params: project_id?, assigned_to?, status?, priority?
// POST   /api/tasks         body: { task_name, description, status, priority, project_id, assigned_to }
// PUT    /api/tasks/:id
// DELETE /api/tasks/:id     Admin|Manager only
export const tasksAPI = {
  list:   (params)    => API.get('/tasks', { params }),
  get:    (id)        => API.get(`/tasks/${id}`),
  create: (data)      => API.post('/tasks', data),
  update: (id, data)  => API.put(`/tasks/${id}`, data),
  complete: (id, data) => API.patch(`/tasks/${id}/complete`, data),
  verify: (id, data)  => API.patch(`/tasks/${id}/verify`, data),
  delete: (id)        => API.delete(`/tasks/${id}`),
  getActiveTimer: ()  => API.get('/tasks/timer/active'),
  startTimer: (id)    => API.post(`/tasks/${id}/timer/start`),
  stopTimer: (id)     => API.post(`/tasks/${id}/timer/stop`),
};

// ── Notifications ─────────────────────────────────────────────────────────────
// GET    /api/notifications
// PUT    /api/notifications/:id/read
// PUT    /api/notifications/read-all
export const notificationsAPI = {
  list:     ()      => API.get('/notifications'),
  getAll:   ()      => API.get('/notifications/all'),
  markRead: (id)    => API.put(`/notifications/${id}/read`),
  markAll:  ()      => API.put('/notifications/read-all'),
};

// ── Time Logs ─────────────────────────────────────────────────────────────────
// Model: log_id | user_id(auto-JWT) | task_id | hours_spent | date | notes
// IMPORTANT: field is "hours_spent" NOT "hours"
//            id is "log_id" NOT "timelog_id"
//            notes NOT description
// POST   /api/timelogs      body: { task_id, hours_spent, date, notes }
// PUT    /api/timelogs/:id  body: { hours_spent, date, notes }  (task_id cannot change)
// DELETE /api/timelogs/:id  owner or Admin only
export const timeLogsAPI = {
  list:   (params)    => API.get('/timelogs', { params }),
  get:    (id)        => API.get(`/timelogs/${id}`),
  create: (data)      => API.post('/timelogs', data),
  update: (id, data)  => API.put(`/timelogs/${id}`, data),
  delete: (id)        => API.delete(`/timelogs/${id}`),
};

// ── Leads ─────────────────────────────────────────────────────────────────────
// Model: lead_id | name | email | phone | status | source
// Status: New | Contacted | Qualified | Unqualified | Converted
// Source: Website | Referral | Social Media | Email | Cold Call | Other
export const leadsAPI = {
  list:   (params)    => API.get('/leads', { params }),
  get:    (id)        => API.get(`/leads/${id}`),
  create: (data)      => API.post('/leads', data),
  update: (id, data)  => API.put(`/leads/${id}`, data),
  delete: (id)        => API.delete(`/leads/${id}`),
};

// ── Deals ─────────────────────────────────────────────────────────────────────
// Model: deal_id | lead_id | title | value | stage | closing_date
// Stage: Prospect | Proposal | Negotiation | Won | Lost
// NOTE: setting stage=Won auto-converts the linked lead status to Converted
export const dealsAPI = {
  list:   (params)    => API.get('/deals', { params }),
  get:    (id)        => API.get(`/deals/${id}`),
  create: (data)      => API.post('/deals', data),
  update: (id, data)  => API.put(`/deals/${id}`, data),
  delete: (id)        => API.delete(`/deals/${id}`),
};

// ── Activities ────────────────────────────────────────────────────────────────
// Model: activity_id | user_id(auto-JWT) | customer_id(REQUIRED) | type | subject | notes | date
// Type ENUM (LOWERCASE): call | email | meeting | note
// IMPORTANT: type must be lowercase. customer_id is required.
// POST /api/activities  body: { customer_id, type, subject, notes, date }
// PUT  /api/activities/:id
// DELETE /api/activities/:id  Admin|Manager only
export const activitiesAPI = {
  list:   (params)    => API.get('/activities', { params }),
  get:    (id)        => API.get(`/activities/${id}`),
  create: (data)      => API.post('/activities', data),
  update: (id, data)  => API.put(`/activities/${id}`, data),
  delete: (id)        => API.delete(`/activities/${id}`),
};

// ── Sprints ─────────────────────────────────────────────────────────────────────
export const sprintsAPI = {
  list:   ()          => API.get('/sprints'),
  create: (data)      => API.post('/sprints', data),
};

// ── Teams ─────────────────────────────────────────────────────────────────────
export const teamsAPI = {
  list:   ()          => API.get('/teams'),
  get:    (id)        => API.get(`/teams/${id}`),
  create: (data)      => API.post('/teams', data),
  update: (id, data)  => API.put(`/teams/${id}`, data),
};

// ── Performance ───────────────────────────────────────────────────────────────
export const performanceAPI = {
  getUserRating:   (id) => API.get(`/performance/user/${id}`),
  getTeamRatings:  (id) => API.get(`/performance/team/${id}`),
};


// ── Users ─────────────────────────────────────────────────────────────────────
// GET    /api/users      Admin|Manager only
// PUT    /api/users/:id  body: { name, email, role }
// DELETE /api/users/:id  Admin|Manager only
export const usersAPI = {
  list:   (params)    => API.get('/users', { params }),
  create: (data)      => API.post('/users', data),
  get:    (id)        => API.get(`/users/${id}`),
  update: (id, data)  => API.put(`/users/${id}`, data),
  delete: (id)        => API.delete(`/users/${id}`),
};

// ── Uploads ───────────────────────────────────────────────────────────────────
export const uploadAPI = {
  single: (formData)  => API.post('/uploads/single', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export default API;
